import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, Loader2, FileText } from "lucide-react";

const field = "bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none w-full";
const EMPTY = { title: "", board: "CBSE", grade_level: "", subject: "", chapter_number: "", chapter_title: "", description: "", class_ids: [], allow_download: true };

const ClassPicker = ({ classes, value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="tb-class-picker">
    {classes.length === 0 && <span className="text-xs text-slate-500">No class groups yet.</span>}
    {classes.map((c) => {
      const on = value.includes(c.id);
      return (
        <button type="button" key={c.id} onClick={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`}
          data-testid={`tb-class-chip-${c.id}`}>{c.name}</button>
      );
    })}
  </div>
);

export const UploadForm = ({ meta, classes, subjects, onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a PDF file");
    if (!form.grade_level && form.class_ids.length === 0) return toast.error("Pick a class level or at least one class group");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => {
      if (k === "class_ids") fd.append(k, JSON.stringify(v));
      else if (!(k === "chapter_number" && v === "")) fd.append(k, String(v));
    });
    setBusy(true);
    setPct(0);
    try {
      await api.post("/textbooks", fd, { onUploadProgress: (p) => p.total && setPct(Math.round((p.loaded * 100) / p.total)) });
      toast.success("Textbook uploaded");
      setForm(EMPTY);
      setFile(null);
      onDone();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="qs-card p-6 mb-6 space-y-4" data-testid="textbook-upload-form">
      <label className="flex items-center gap-3 border-2 border-dashed border-slate-200 rounded-2xl p-5 cursor-pointer hover:border-indigo-400 transition-colors" data-testid="tb-file-dropzone">
        <FileText className="w-8 h-8 text-indigo-500" />
        <span className="text-sm text-slate-600 flex-1 truncate">{file ? `${file.name} · ${(file.size / 1048576).toFixed(1)} MB` : "Click to choose a PDF (max 50 MB)"}</span>
        <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="tb-file-input" />
      </label>
      <div className="grid md:grid-cols-2 gap-3">
        <input required value={form.title} onChange={set("title")} placeholder="Title (e.g. NCERT Mathematics)" className={field} data-testid="tb-title-input" />
        <input required list="tb-subjects" value={form.subject} onChange={set("subject")} placeholder="Subject" className={field} data-testid="tb-subject-input" />
        <datalist id="tb-subjects">{subjects.map((s) => <option key={s.id} value={s.name} />)}</datalist>
        <select value={form.board} onChange={set("board")} className={field} data-testid="tb-board-select">
          {meta.boards.map((b) => <option key={b}>{b}</option>)}
        </select>
        <select value={form.grade_level} onChange={set("grade_level")} className={field} data-testid="tb-grade-select">
          <option value="">No class level (class groups only)</option>
          {meta.grades.map((g) => <option key={g} value={g}>Class {g}</option>)}
        </select>
        <input type="number" min={0} max={999} value={form.chapter_number} onChange={set("chapter_number")} placeholder="Chapter no. (optional)" className={field} data-testid="tb-chapter-number-input" />
        <input value={form.chapter_title} onChange={set("chapter_title")} placeholder="Chapter title (optional)" className={field} data-testid="tb-chapter-title-input" />
      </div>
      <textarea rows={2} value={form.description} onChange={set("description")} placeholder="Short description (optional)" className={field} data-testid="tb-description-input" />
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Also share with class groups</p>
        <ClassPicker classes={classes} value={form.class_ids} onChange={(v) => setForm({ ...form, class_ids: v })} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={form.allow_download} onCheckedChange={(v) => setForm({ ...form, allow_download: v })} data-testid="tb-allow-download-switch" />
          Students can download
        </label>
        <button disabled={busy} className="btn-primary text-sm" data-testid="tb-upload-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload textbook
        </button>
      </div>
      {busy && <Progress value={pct} className="h-2" data-testid="tb-upload-progress" />}
    </form>
  );
};
